import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, SparklesIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { contentGenerationService, ContentGenerationRequest } from '../../../services/contentGenerationService';
import { useToast } from '../../UI/ToastProvider';

interface AIContentGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContentGenerated: (content: string) => void;
  documentType?: string;
}

export const AIContentGenerationModal: React.FC<AIContentGenerationModalProps> = ({
  isOpen,
  onClose,
  onContentGenerated,
  documentType = 'documento'
}) => {
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [style, setStyle] = useState<'formal' | 'informal' | 'technical' | 'creative'>('formal');
  const [maxLength, setMaxLength] = useState(400);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const { addToast } = useToast();

  const handleGenerateContent = async () => {
    if (!prompt.trim()) {
      addToast('Por favor, insira um prompt para gerar o conteúdo', 'error');
      return;
    }

    if (prompt.length > 500) {
      addToast('O prompt não pode exceder 500 caracteres', 'error');
      return;
    }

    if (context.length > 200) {
      addToast('O contexto não pode exceder 200 caracteres', 'error');
      return;
    }

    if (!contentGenerationService.isConfigured()) {
      addToast('Serviço de IA não configurado. Verifique as configurações do OpenAI.', 'error');
      return;
    }

    setIsGenerating(true);
    setGeneratedContent('');

    try {
      const request: ContentGenerationRequest = {
        prompt: prompt.trim(),
        documentType,
        context: context.trim() || undefined,
        maxLength,
        style
      };

      const response = await contentGenerationService.generateContent(request);

      if (response.success) {
        setGeneratedContent(response.content);
        addToast('Conteúdo gerado com sucesso!', 'success');
      } else {
        addToast(`Erro ao gerar conteúdo: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Erro ao gerar conteúdo:', error);
      addToast('Erro inesperado ao gerar conteúdo', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseContent = () => {
    if (generatedContent.trim()) {
      onContentGenerated(generatedContent);
      onClose();
      // Reset do estado
      setPrompt('');
      setContext('');
      setStyle('formal');
      setMaxLength(400);
      setGeneratedContent('');
    }
  };

  const handleClose = () => {
    onClose();
    // Reset do estado
    setPrompt('');
    setContext('');
    setStyle('formal');
    setMaxLength(400);
    setGeneratedContent('');
  };

  const isConfigured = contentGenerationService.isConfigured();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center">
                    <SparklesIcon className="h-8 w-8 text-purple-600 mr-3" />
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                      Criar Documento com IA
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {!isConfigured && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Configuração de IA não encontrada
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>Para usar esta funcionalidade, configure as seguintes variáveis de ambiente:</p>
                          <ul className="list-disc list-inside mt-1">
                            <li><code className="bg-yellow-100 px-1 rounded">REACT_APP_OPENAI_API_KEY</code> - Sua chave da API OpenAI</li>
                            <li><code className="bg-yellow-100 px-1 rounded">REACT_APP_OPENAI_MODEL</code> - Modelo a ser usado (padrão: gpt-5-mini)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Painel de configuração */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prompt para IA <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Descreva o conteúdo que você gostaria que a IA gere para o seu documento..."
                        className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={!isConfigured}
                        maxLength={500}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">
                          {prompt.length}/500 caracteres
                        </span>
                        {prompt.length > 450 && (
                          <span className="text-xs text-orange-500">
                            {prompt.length > 480 ? 'Limite próximo!' : 'Ainda há espaço'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contexto adicional (opcional)
                      </label>
                      <textarea
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        placeholder="Informações adicionais que podem ajudar a IA a gerar conteúdo mais relevante..."
                        className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        disabled={!isConfigured}
                        maxLength={200}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500">
                          {context.length}/200 caracteres
                        </span>
                        {context.length > 180 && (
                          <span className="text-xs text-orange-500">
                            {context.length > 190 ? 'Limite próximo!' : 'Ainda há espaço'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estilo do documento
                        </label>
                        <select
                          value={style}
                          onChange={(e) => setStyle(e.target.value as any)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          disabled={!isConfigured}
                        >
                          <option value="formal">Formal</option>
                          <option value="informal">Informal</option>
                          <option value="technical">Técnico</option>
                          <option value="creative">Criativo</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Comprimento máximo (palavras)
                        </label>
                        <input
                          type="number"
                          value={maxLength}
                          onChange={(e) => setMaxLength(Number(e.target.value))}
                          min="100"
                          max="700"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          disabled={!isConfigured}
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleGenerateContent}
                      disabled={!isConfigured || isGenerating || !prompt.trim()}
                      className="w-full flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Gerando conteúdo...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-5 w-5 mr-2" />
                          Gerar Conteúdo com IA
                        </>
                      )}
                    </button>

                    {/* Removido o bloco de informações de configuração */}
                  </div>

                  {/* Painel de resultado */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Conteúdo gerado
                      </label>
                      {generatedContent && (
                        <button
                          onClick={handleUseContent}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1" />
                          Usar Conteúdo
                        </button>
                      )}
                    </div>
                    
                    <div className="h-96 border border-gray-300 rounded-md p-3 bg-gray-50 overflow-y-auto">
                      {generatedContent ? (
                        <div className="prose prose-sm max-w-none">
                          <div className="whitespace-pre-wrap text-gray-800">{generatedContent}</div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <SparklesIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p>O conteúdo gerado pela IA aparecerá aqui</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}; 